/* eslint-disable unused-imports/no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { yupResolver } from "@hookform/resolvers/yup";
import { ArrowBack, Refresh } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { practiceUsersApi, roleManagementApi } from "apis";
import BranchesSection from "components/features/profile/BranchesSection";
import PracticeInformationSection from "components/features/profile/PracticeInformationSection";
import DashboardLayout from "components/layouts/DashboardLayout";
import { EPath } from "constants/path";
import { useToast } from "contexts/ToastContext";
import { FirebaseStorageService } from "helpers/firebaseStorage";
import {
  BranchDto,
  InviteProviderUserRequestDto,
  RoleLightDto,
} from "libs/cellera-sdk";
import { SelectBranchModal } from "pages/components/SelectBranchModal";
import { useEffect, useRef, useState, useCallback } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import {
  InviteProviderFormData,
  inviteProviderSchema,
} from "validations/inviteProviderSchemas";

function InviteProviderPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const initialFormDataRef = useRef<InviteProviderFormData | null>(null);

  const [currentProfileImageUrl, setCurrentProfileImageUrl] = useState<
    string | undefined
  >();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState<BranchDto | undefined>(
    undefined
  );
  const [branchModalOpen, setBranchModalOpen] = useState(false);

  // Fetch roles for dropdown
  const { data: rolesData } = useQuery({
    queryKey: ["roles"],
    queryFn: async () => {
      const response = await roleManagementApi.getPracticeRoles();
      return response;
    },
    retry: 2,
  });

  const roles = rolesData || [];

  const createMutation = useMutation({
    mutationFn: async (formData: InviteProviderFormData) => {
      // Handle profile image upload if it's a File object
      let profileImageUrl = undefined;
      if (formData.profileImage instanceof File) {
        try {
          profileImageUrl = await FirebaseStorageService.uploadProfileImage(
            formData.profileImage
          );
          setCurrentProfileImageUrl(profileImageUrl);
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Failed to upload profile image. Please try again.";
          throw new Error(errorMessage);
        }
      }

      // Create practice profile data
      const requestData: InviteProviderUserRequestDto = {
        email: formData.email,
        specialties: formData.specialties as string[],
        firstName: formData.firstName,
        middleName: formData.middleName,
        lastName: formData.lastName,
        phoneNumber: formData.phoneNumber ?? "",
        npi: formData.npi ?? "",
        profileImage: profileImageUrl,
        branchId: formData.branchId ?? undefined,
        roleId: formData.roleId ?? undefined,
      };

      return await practiceUsersApi.inviteProviderUser({
        inviteProviderUserRequestDto: requestData,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["practiceUsers"],
      });
      showSuccess(
        "Practice Created",
        "New practice has been created successfully."
      );
      navigate(EPath.PRACTICE_MANAGEMENT);
    },
    onError: (error) => {
      throw error;
    },
  });

  const inviteProviderForm = useForm({
    resolver: yupResolver(inviteProviderSchema),
    defaultValues: {
      email: "",
      profileImage: null,
      firstName: "",
      middleName: "",
      lastName: "",
      phoneNumber: "",
      npi: "",
      specialties: [],
      branchId: "",
      roleId: "",
    },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  // Function to compare form data and detect changes
  const checkForChanges = useCallback((currentData: InviteProviderFormData) => {
    if (!initialFormDataRef.current) return false;

    const initial = initialFormDataRef.current;

    return (
      initial.firstName !== currentData.firstName ||
      initial.middleName !== currentData.middleName ||
      initial.lastName !== currentData.lastName ||
      initial.phoneNumber !== currentData.phoneNumber ||
      initial.npi !== currentData.npi ||
      JSON.stringify(initial.specialties) !==
        JSON.stringify(currentData.specialties) ||
      initial.profileImage !== currentData.profileImage ||
      initial.email !== currentData.email ||
      initial.branchId !== currentData.branchId ||
      initial.roleId !== currentData.roleId
    );
  }, []);

  // Watch for form changes
  const watchedValues = inviteProviderForm.watch();

  useEffect(() => {
    if (initialFormDataRef.current) {
      const hasChanges = checkForChanges(watchedValues);
      setHasUnsavedChanges(hasChanges);
    }
  }, [watchedValues, checkForChanges]);

  // Set initial form data reference
  useEffect(() => {
    const formData: InviteProviderFormData = {
      email: "",
      profileImage: null,
      firstName: "",
      middleName: "",
      lastName: "",
      phoneNumber: "",
      npi: "",
      specialties: [],
      branchId: "",
      roleId: "",
    };

    inviteProviderForm.reset(formData);
    initialFormDataRef.current = formData;
    setCurrentProfileImageUrl(undefined);
    setHasUnsavedChanges(false);

    // Trigger validation after reset
    setTimeout(() => {
      inviteProviderForm.trigger();
    }, 100);
  }, [inviteProviderForm]);

  // Navigation blocking
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleNavigation = useCallback(
    (path: string) => {
      if (hasUnsavedChanges) {
        const confirmed = window.confirm(
          "You have unsaved changes. Are you sure you want to leave?"
        );
        if (confirmed) {
          navigate(path);
        }
      } else {
        navigate(path);
      }
    },
    [hasUnsavedChanges, navigate]
  );

  const onSubmit = useCallback(
    async (data: InviteProviderFormData) => {
      data.branchId = selectedBranch?.id;
      try {
        await createMutation.mutateAsync(data);
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to invite practice. Please try again.";
        showError("Invite Practice Failed", errorMessage);
      }
    },
    [selectedBranch, createMutation, showError]
  );

  useEffect(() => {
    if (selectedBranch) {
      inviteProviderForm.setValue("branchId", selectedBranch.id || "");
      inviteProviderForm.trigger("branchId");
    } else {
      inviteProviderForm.setValue("branchId", "");
      inviteProviderForm.trigger("branchId");
    }
  }, [selectedBranch, inviteProviderForm]);

  return (
    <DashboardLayout title="Invite Practice" onNavigate={handleNavigation}>
      <Box className="flex items-center justify-between mb-4">
        <Breadcrumbs separator="/" aria-label="breadcrumb">
          <Link
            underline="hover"
            color="inherit"
            onClick={() => handleNavigation(EPath.PROVIDER_DASHBOARD)}
            className="cursor-pointer"
          >
            Dashboard
          </Link>
          <Link
            underline="hover"
            color="inherit"
            onClick={() => handleNavigation(EPath.PRACTICE_MANAGEMENT)}
            className="cursor-pointer"
          >
            Staff management
          </Link>
          <Typography color="text.primary">Invite Practice</Typography>
        </Breadcrumbs>
        <Box className="flex items-center gap-1">
          <Tooltip title="Back">
            <IconButton
              size="small"
              onClick={() => handleNavigation(EPath.PRACTICE_MANAGEMENT)}
            >
              <ArrowBack fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton
              size="small"
              onClick={() => window.location.reload()}
              disabled={createMutation.isPending}
            >
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <FormProvider {...inviteProviderForm}>
        <form
          onSubmit={inviteProviderForm.handleSubmit(onSubmit)}
          className="relative flex flex-col gap-4"
        >
          <PracticeInformationSection
            profileImage={currentProfileImageUrl ?? ""}
            isInvite
          />
          {/* Role Selection */}
          <Box className="mt-8">
            <Typography variant="h6" className="text-xl font-medium">
              Select a Role
            </Typography>
            <Box className="grid grid-cols-3 mt-6">
              <FormControl fullWidth>
                <InputLabel id="role-select-label">Role</InputLabel>
                <Select
                  labelId="role-select-label"
                  id="role-select"
                  value={inviteProviderForm.watch("roleId") || ""}
                  label="Role"
                  onChange={(e) => {
                    inviteProviderForm.setValue("roleId", e.target.value);
                    inviteProviderForm.trigger("roleId");
                  }}
                  error={!!inviteProviderForm.formState.errors.roleId}
                >
                  {roles.map((role: RoleLightDto) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
                {inviteProviderForm.formState.errors.roleId && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, ml: 1.5 }}
                  >
                    {inviteProviderForm.formState.errors.roleId.message}
                  </Typography>
                )}
              </FormControl>
            </Box>
          </Box>
          <BranchesSection
            rowsPosition={"relative"}
            branches={selectedBranch ? [selectedBranch] : []}
            minHeight={200}
            onAddBranch={() => {
              setBranchModalOpen(true);
            }}
          />
          <Box
            className="flex justify-center gap-4 mt-8"
            sx={{ position: "relative", zIndex: 10 }}
          >
            <Button
              type="submit"
              variant="contained"
              className="px-6 py-2 rounded-full w-full"
              disabled={
                createMutation.isPending ||
                !inviteProviderForm.formState.isValid
              }
            >
              {createMutation.isPending ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Invite"
              )}
            </Button>
            <Button
              type="button"
              variant="outlined"
              className="px-6 py-2 rounded-full w-full"
              onClick={() => {
                inviteProviderForm.reset();
                setSelectedBranch(undefined);
                setCurrentProfileImageUrl(undefined);
                setHasUnsavedChanges(false);
              }}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
          </Box>
        </form>
      </FormProvider>

      <SelectBranchModal
        open={branchModalOpen}
        onClose={() => setBranchModalOpen(false)}
        onSelect={(branch?: BranchDto) => {
          if (branch) {
            setSelectedBranch(branch);
            inviteProviderForm.setValue("branchId", branch.id || "");
          }
          setBranchModalOpen(false);
        }}
        isLoading={createMutation.isPending}
      />
    </DashboardLayout>
  );
}

export default InviteProviderPage;
